import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "@fg/icons";
import { connectParityApi, type UnfurlPreview } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";

/**
 * Renders Teams-style "rich link preview" cards under any message
 * whose body contains an http(s) URL. Up to 2 cards per message —
 * channels with chatty link sharing don't need a wall of previews.
 *
 * Each card pulls its data from /api/unfurl (cached server-side for
 * 15 min). The card is best-effort: if the OG fetch failed the row
 * just shows the bare URL + site host without a title or image.
 */
export function UnfurlPreviews({ bodyHtml }: { bodyHtml: string }) {
    const urls = useMemo(() => extractUrls(bodyHtml).slice(0, 2), [bodyHtml]);
    if (urls.length === 0) return null;
    return (
        <div className="mt-1 flex flex-col gap-1.5">
            {urls.map((url) => (
                <UnfurlCard key={url} url={url} />
            ))}
        </div>
    );
}

function UnfurlCard({ url }: { url: string }) {
    const token = useAccessToken();
    const q = useQuery({
        queryKey: ["unfurl", url, token],
        queryFn: () => connectParityApi.unfurl(url, token),
        enabled: Boolean(token),
        // Stale long — server cache already covers most of this.
        staleTime: 60 * 60_000
    });
    if (!q.data) return null; // skip while loading; don't flicker placeholder cards
    const u = q.data as UnfurlPreview;
    // No useful metadata returned — skip the card so we don't render a
    // bare URL chip below every chat message that links to a JSON API.
    if (!u.title && !u.description && !u.image) return null;
    return (
        <a
            href={u.url}
            target="_blank"
            rel="noreferrer noopener"
            className="flex max-w-xl gap-3 overflow-hidden rounded-md border bg-card p-2 hover:border-primary/40 hover:shadow-sm"
        >
            {u.image ? (
                <img
                    src={u.image}
                    alt=""
                    className="h-20 w-28 shrink-0 rounded object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                />
            ) : null}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <ExternalLink className="h-3 w-3" aria-hidden />
                    {u.siteName ?? hostOf(u.url)}
                </div>
                {u.title ? (
                    <div className="line-clamp-2 text-sm font-medium">{u.title}</div>
                ) : null}
                {u.description ? (
                    <p className="line-clamp-2 text-xs text-muted-foreground">{u.description}</p>
                ) : null}
            </div>
        </a>
    );
}

function extractUrls(html: string): string[] {
    // Strip tags first so we don't grab meta hrefs or asset URLs out
    // of the raw markup. Then match http(s) URLs in the resulting
    // plaintext.
    const text = html.replace(/<[^>]+>/g, " ");
    const found = text.match(/https?:\/\/[^\s<>"'`]+/g) ?? [];
    // Trim trailing punctuation that's almost certainly not part of the
    // URL (commas, periods, parens, etc.) — common in casual chat.
    const cleaned = found.map((u) => u.replace(/[),.;:!?\]\}]+$/g, ""));
    // Dedupe while preserving order.
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const u of cleaned) {
        if (!seen.has(u)) {
            seen.add(u);
            unique.push(u);
        }
    }
    return unique;
}

function hostOf(url: string): string {
    try { return new URL(url).host; } catch { return url; }
}
